import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { ChevronRight, Camera, Eye } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import GalleryCard from "../components/GalleryCard";
import Lightbox from "../components/Lightbox";
import { GALLERY_CATS, GALLERY_TX } from "../data/gallery";
import { useContent } from "../context/ContentContext";

const Gallery = () => {
  const { t, lang } = useLang();
  const { GALLERY, IMAGES } = useContent();
  const tx = GALLERY_TX[lang];
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(-1);
  const [cursorOn, setCursorOn] = useState(false);
  const cx = useMotionValue(-100);
  const cy = useMotionValue(-100);
  const scx = useSpring(cx, { stiffness: 400, damping: 30 });
  const scy = useSpring(cy, { stiffness: 400, damping: 30 });

  const items = useMemo(() => (cat === "all" ? GALLERY : GALLERY.filter((g) => g.cat === cat)), [cat, GALLERY]);

  const onHover = (e, on) => {
    if (e) { cx.set(e.clientX); cy.set(e.clientY); }
    setCursorOn(on);
  };

  return (
    <div data-testid="gallery-page">
      <section className="relative overflow-hidden bg-jannah-green-dark">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.heroSlides[2]})` }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20 md:pt-28 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-sm text-white/70">
            <Link to="/" className="hover:text-white">{t.nav.home}</Link> <ChevronRight size={14} /> <span className="text-jannah-gold-light">{tx.crumb}</span>
          </motion.div>
          <motion.span initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="eyebrow text-jannah-gold-light mt-6 inline-flex items-center gap-2"><Camera size={14} /> {tx.eyebrow}</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }} className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-white">{tx.title}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }} className="mt-5 text-base md:text-lg text-white/80 max-w-2xl">{tx.sub}</motion.p>
        </div>
      </section>

      <section className="section-pad bg-jannah-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
              {GALLERY_CATS.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCat(c.key)}
                  data-testid={`gallery-filter-${c.key}`}
                  className={`relative px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${cat === c.key ? "text-white" : "text-jannah-ink/70 bg-white border border-jannah-sand hover:border-jannah-green/40 hover:text-jannah-green"}`}
                >
                  {cat === c.key && <motion.span layoutId="gallery-pill" className="absolute inset-0 rounded-full bg-jannah-green shadow-md" transition={{ type: "spring", stiffness: 400, damping: 30 }} />}
                  <span className="relative">{c[lang]}</span>
                </button>
              ))}
            </div>
            <span className="text-sm text-jannah-ink/60" data-testid="gallery-count">{tx.count(items.length)}</span>
          </div>

          <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5">
            <AnimatePresence mode="popLayout">
              {items.map((g) => (
                <GalleryCard key={g.id} item={g} title={g[lang]} tx={tx} onOpen={(it) => setOpen(items.findIndex((x) => x.id === it.id))} onHover={onHover} />
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <motion.div
        style={{ x: scx, y: scy }}
        animate={{ opacity: cursorOn ? 1 : 0, scale: cursorOn ? 1 : 0.5 }}
        transition={{ duration: 0.2 }}
        className="hidden md:flex fixed top-0 left-0 z-[60] -translate-x-1/2 -translate-y-1/2 pointer-events-none w-20 h-20 rounded-full bg-jannah-gold/90 backdrop-blur text-white items-center justify-center shadow-2xl"
        data-testid="gallery-cursor"
      >
        <div className="flex flex-col items-center text-[10px] font-bold uppercase tracking-widest"><Eye size={18} /> {tx.view}</div>
      </motion.div>

      <Lightbox items={items} index={open} onClose={() => setOpen(-1)} onIndex={setOpen} lang={lang} tx={tx} />
    </div>
  );
};

export default Gallery;
